import type { CalendarEvent } from '@akem/shared';
import {
    CalendarToday as CalendarIcon,
    AttachMoney as DividendIcon,
    ShowChart as EarningsIcon,
    Description as FilingIcon,
    CallSplit as SplitIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    styled,
    tooltipClasses
} from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { marketService } from '../../../services/marketService.js';
import { getProfileEvents } from '../../../services/profile.js';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';
import type { WidgetProps } from '../types/index.js';

// --- Constants & Config ---

const typeConfig: Record<
  CalendarEvent['type'],
  {
    label: string;
    color: string; // Main accent color
    icon: React.ElementType;
  }
> = {
  event: {
    label: 'Macro',
    color: '#42a5f5', // Blue
    icon: CalendarIcon,
  },
  earnings: {
    label: 'Earnings',
    color: '#ab47bc', // Purple
    icon: EarningsIcon,
  },
  dividend: {
    label: 'Dividends',
    color: '#66bb6a', // Green
    icon: DividendIcon,
  },
  filing: {
    label: 'Filings',
    color: '#ffa726', // Orange
    icon: FilingIcon,
  },
  split: {
    label: 'Splits',
    color: '#ef5350', // Red/Orange
    icon: SplitIcon,
  },
};

const typeOrder: CalendarEvent['type'][] = ['event', 'earnings', 'dividend', 'filing', 'split'];
const DEFAULT_LOOKAHEAD_DAYS = 14;
const TIMELINE_DAYS = 7;
const DARK_BASE = '#14121a';
const DARK_SURFACE = '#20202e';
const DARK_PANEL = '#17161f';

// --- Styled Components ---

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2a2a38',
    color: '#fff',
    maxWidth: 300,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid rgba(255, 255, 255, 0.1)',
    padding: 0,
    borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2a2a38',
  },
}));

// --- Helpers ---

const getTypeIcon = (type: CalendarEvent['type']) => {
  const config = typeConfig[type];
  const IconComponent = config.icon;
  return <IconComponent fontSize="inherit" sx={{ color: config.color }} />;
};

const startOfDayKey = (value: number | Date) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const formatRelativeTime = (ts: number) => {
  const diff = ts - Date.now();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < 0) return 'Passed';

  const days = Math.floor(diff / day);
  const hours = Math.floor((diff % day) / hour);
  const minutes = Math.floor((diff % hour) / minute);

  if (days > 0) return `In ${days}d : ${hours}h`;
  if (hours > 0) return `In ${hours}h : ${minutes}m`;
  return `In ${minutes}m`;
};

const formatDividendFrequency = (frequency: string | number | undefined): string | undefined => {
  if (!frequency) return undefined;
  if (typeof frequency === 'number' || !isNaN(Number(frequency))) {
    const numFreq = typeof frequency === 'number' ? frequency : Number(frequency);
    switch (numFreq) {
      case 0: return 'one-time';
      case 1: return 'annual';
      case 2: return 'semi-annual';
      case 4: return 'quarterly';
      case 12: return 'monthly';
      default: return `${numFreq}/year`;
    }
  }
  return frequency.toString();
};

// --- Components ---

export const CalendarWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs, onUpdateState }) => {
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [fetchError, setFetchError] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalFocusEventId, setModalFocusEventId] = React.useState<string | null>(null);
  
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const selectedEventOutput = React.useMemo(() => {
    return widget.outputs.find((output) => output.id === 'selectedEvent')?.value as CalendarEvent | undefined;
  }, [widget.outputs]);
  
  const { openProfileByTicker } = useProfileNavigation();

  // --- Data Fetching ---
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(false);
    (async () => {
      try {
        let data: CalendarEvent[] = [];
        if (profileId) {
          data = await getProfileEvents(profileId).catch(() => []);
        } else {
          const now = new Date();
          const from = new Date(now);
          const to = new Date(now);
          to.setDate(now.getDate() + DEFAULT_LOOKAHEAD_DAYS);
          data = await marketService
            .getMarketCalendar({ from: from.toISOString(), to: to.toISOString() })
            .catch(() => []);
        }
        if (!cancelled) {
          setEvents(data);
          onUpdateState({ loading: false, data, error: undefined });
        }
      } catch {
        if (!cancelled) {
          setEvents([]);
          setFetchError(true);
          onUpdateState({ loading: false, data: [], error: "Couldn't fetch events." });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId, onUpdateState]);

  // --- Selection Logic ---
  React.useEffect(() => {
    if (selectedEventOutput?.id && selectedEventOutput.id !== selectedId) {
      setSelectedId(selectedEventOutput.id);
    }
  }, [selectedEventOutput, selectedId]);

  const handleSelect = React.useCallback(
    (event: CalendarEvent) => {
      setSelectedId(event.id);
      onUpdateOutputs({ selectedEvent: event });
    },
    [onUpdateOutputs]
  );

  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setModalFocusEventId(null);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false);
    setModalFocusEventId(null);
  }, []);

  const handleTickerNavigation = React.useCallback(
    (ticker?: string | null, event?: React.MouseEvent) => {
      const symbol = typeof ticker === 'string' ? ticker.trim() : '';
      if (!symbol) return;
      event?.stopPropagation();
      openProfileByTicker(symbol, { target: 'dashboard' });
    },
    [openProfileByTicker]
  );

  // --- Derived Data ---
  const upNext = React.useMemo(() => {
    if (events.length === 0) return undefined;
    const now = Date.now();
    const sorted = events.filter(e => e.ts >= now).sort((a, b) => a.ts - b.ts);
    return sorted[0] ?? events[events.length - 1];
  }, [events]);

  const timelineDays = React.useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: TIMELINE_DAYS }, (_, i) => {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      return {
        key: day.getTime(),
        date: day,
        dayNum: day.getDate(),
        dayName: day.toLocaleDateString('en-US', { weekday: 'long' }),
        monthName: day.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      };
    });
  }, []);

  const eventsByDay = React.useMemo(() => {
    const map = new Map<number, Record<CalendarEvent['type'], CalendarEvent[]>>();
    for (const item of events) {
      const key = startOfDayKey(item.ts);
      const entry = map.get(key) ?? {
        event: [],
        earnings: [],
        dividend: [],
        filing: [],
        split: [],
      };
      entry[item.type].push(item);
      map.set(key, entry);
    }
    return map;
  }, [events]);

  const timeFormatter = React.useMemo(() => new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }), []);
  const longDateFormatter = React.useMemo(() => new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' }), []);

  // --- Render Helpers ---
  const renderUpNext = () => {
    if (!upNext) return null;
    const config = typeConfig[upNext.type];
    
    return (
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: '#1b1b26',
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Left Accent Bar */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: config.color,
            boxShadow: `0 0 10px ${config.color}`,
          }}
        />

        {/* Header Row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
            Up Next
          </Typography>
          <Stack alignItems="flex-end">
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500, fontSize: '1rem' }}>
              {formatRelativeTime(upNext.ts)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {longDateFormatter.format(new Date(upNext.ts))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {timeFormatter.format(new Date(upNext.ts))}
            </Typography>
          </Stack>
        </Stack>

        {/* Content Row */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: -1 }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
              {upNext.label}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              {upNext.metadata?.symbol && (
                <Chip 
                  label={upNext.metadata.symbol} 
                  size="small" 
                  onClick={(event) => handleTickerNavigation(upNext.metadata?.symbol, event)}
                  clickable
                  sx={{ 
                    bgcolor: alpha(config.color, 0.15), 
                    color: config.color, 
                    fontWeight: 600,
                    borderRadius: 1,
                    height: 20,
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                  }} 
                />
              )}
              <Typography variant="body2" sx={{ color: config.color }}>
                {/* Specific Metadata Display */}
                {upNext.type === 'dividend' && upNext.metadata?.amount && `$${upNext.metadata.amount.toFixed(2)} ${formatDividendFrequency(upNext.metadata.frequency) || ''}`}
                {upNext.type === 'earnings' && upNext.metadata?.consensusEPS && `Est. EPS: $${upNext.metadata.consensusEPS.toFixed(2)}`}
                {upNext.type === 'event' && upNext.metadata?.importance && `${upNext.metadata.importance.toUpperCase()} IMPACT`}
                {upNext.type === 'filing' && upNext.metadata?.formType && `Form ${upNext.metadata.formType}`}
                {upNext.type === 'split' && upNext.metadata?.ratio && `Ratio: ${upNext.metadata.ratio}`}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      className="nopan nodrag"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DARK_BASE,
        p: 2,
        overflow: 'hidden',
      }}
    >
      {/* Up Next Card */}
      {renderUpNext()}

      {/* Grid Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '120px repeat(5, 1fr)',
          gap: 1,
          mb: 1,
        }}
      >
        <Box /> {/* Date Column Spacer */}
        {typeOrder.map((type) => (
          <Box
            key={type}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: DARK_PANEL,
              borderRadius: 1,
              p: 1,
              borderTop: `2px solid ${typeConfig[type].color}`,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {typeConfig[type].label}
            </Typography>
            {React.createElement(typeConfig[type].icon, { sx: { fontSize: 16, color: typeConfig[type].color } })}
          </Box>
        ))}
      </Box>

      {/* Grid Rows */}
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loading ? (
          <Stack spacing={1}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={90}
                sx={{ bgcolor: DARK_PANEL, borderRadius: 1 }}
              />
            ))}
          </Stack>
        ) : (
          timelineDays.map((day) => {
            const dayEvents = eventsByDay.get(day.key);
            const totalDayEvents = dayEvents ? Object.values(dayEvents).flat().length : 0;

            return (
              <Box
                key={day.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '120px repeat(5, 1fr)',
                  gap: 1,
                  height: 90,
                  minHeight: 90,
                }}
              >
                {/* Date Cell */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1, fontSize: '0.7rem' }}
                  >
                    {day.monthName}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: '#fff', fontWeight: 500, my: 0.25, fontSize: '1.6rem', lineHeight: 1.1 }}
                  >
                    {day.dayNum}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {day.dayName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                    {[...Array(Math.min(totalDayEvents, 3))].map((_, i) => (
                      <Box key={i} sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: i === 0 ? '#4caf50' : '#666' }} />
                    ))}
                  </Stack>
                </Box>

                {/* Event Cells */}
                {typeOrder.map((type) => {
                  const bucket = dayEvents?.[type] ?? [];
                  const count = bucket.length;
                  const config = typeConfig[type];

                  return (
                    <CustomTooltip
                      key={type}
                      title={
                        count > 0 ? (
                          <Box>
                            <Box sx={{ bgcolor: alpha(config.color, 0.2), p: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {React.createElement(config.icon, { sx: { color: config.color, fontSize: 16 } })}
                                <Typography variant="subtitle2" sx={{ color: config.color }}>
                                  {config.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                                  {day.monthName} {day.dayNum}
                                </Typography>
                              </Stack>
                            </Box>
                            <Stack spacing={1} sx={{ p: 1.5 }}>
                              {bucket.map((e) => (
                                <Box key={e.id} onClick={() => handleSelect(e)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
                                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                                    {e.label}
                                  </Typography>
                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">
                                      {timeFormatter.format(new Date(e.ts))}
                                    </Typography>
                                    {e.metadata?.symbol && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: config.color, cursor: 'pointer', fontWeight: 700 }}
                                        onClick={(event) => handleTickerNavigation(e.metadata?.symbol, event)}
                                      >
                                        {e.metadata.symbol}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        ) : ''
                      }
                    >
                      <Box
                        sx={{
                          bgcolor: count > 0 ? alpha(config.color, 0.08) : 'transparent',
                          border: count > 0 ? `1px solid ${alpha(config.color, 0.2)}` : '1px solid rgba(255,255,255,0.02)',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s',
                          cursor: count > 0 ? 'pointer' : 'default',
                          '&:hover': count > 0 ? {
                            bgcolor: alpha(config.color, 0.15),
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(config.color, 0.1)}`,
                          } : {},
                        }}
                        onClick={() => {
                          if (count > 0) {
                            handleSelect(bucket[0]);
                            setModalFocusEventId(bucket[0].id);
                            setModalOpen(true);
                          }
                        }}
                      >
                        {count > 0 && (
                          <>
                            <Typography variant="h3" sx={{ color: config.color, fontWeight: 400, fontSize: '2.5rem', lineHeight: 1 }}>
                              {count}
                            </Typography>
                            <Stack spacing={0.5} alignItems="flex-end">
                              {bucket.slice(0, 3).map((e) => (
                                <Box
                                  key={e.id}
                                  sx={{
                                    bgcolor: alpha('#fff', 0.05),
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.25,
                                    maxWidth: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: e.metadata?.symbol ? config.color : alpha('#fff', 0.7),
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      cursor: e.metadata?.symbol ? 'pointer' : 'default',
                                    }}
                                    noWrap
                                    onClick={
                                      e.metadata?.symbol
                                        ? (event) => handleTickerNavigation(e.metadata?.symbol, event)
                                        : undefined
                                    }
                                  >
                                    {e.metadata?.symbol || e.label.substring(0, 6)}
                                  </Typography>
                                </Box>
                              ))}
                              {count > 3 && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  +{count - 3} more
                                </Typography>
                              )}
                            </Stack>
                          </>
                        )}
                      </Box>
                    </CustomTooltip>
                  );
                })}
              </Box>
            );
          })
        )}
      </Box>

      {/* Detailed Modal (Kept for deep dives) */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: '#1b1726',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Event Details</DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {/* Reuse existing list logic or simplify. For now, simple list. */}
          <List>
            {events.filter(e => modalFocusEventId ? e.id === modalFocusEventId : true).map(item => (
               <ListItem key={item.id}>
                 <ListItemText 
                    primary={item.label} 
                    secondary={new Date(item.ts).toLocaleString()} 
                    primaryTypographyProps={{ color: '#fff' }}
                    secondaryTypographyProps={{ color: 'text.secondary' }}
                 />
               </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
